import { Icon } from "../../components/Icon";
import type { WishPreview } from "../../types/wish";

export type PreviewStatus = "loading" | "success" | "error";

interface OGPreviewProps {
  url: string;
  status?: PreviewStatus;
  preview?: WishPreview | null;
  imageUrl?: string;
  title?: string;
  onRetry?: () => void;
}

function hostnameOf(rawUrl: string): string {
  try {
    return new URL(rawUrl).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
}

export function OGPreview({
  url,
  status = "success",
  preview,
  imageUrl,
  title,
  onRetry,
}: OGPreviewProps) {
  if (url.trim() === "") return null;

  const host = hostnameOf(url);
  const resolvedImage = preview?.image_url ?? imageUrl;
  const resolvedTitle = preview?.title ?? title;
  const hasMetadata = Boolean(resolvedImage || resolvedTitle || preview?.price);

  return (
    <div className="mt-3 flex min-w-0 items-stretch gap-3 rounded-2xl border border-line bg-surface-subtle p-3">
      <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-action-soft text-action ring-1 ring-black/10">
        {status === "loading" ? (
          <span
            data-testid="preview-loader"
            aria-hidden="true"
            className="h-6 w-6 rounded-full border-[3px] border-action border-t-transparent motion-safe:animate-spin"
          />
        ) : resolvedImage ? (
          <img src={resolvedImage} alt="" className="h-full w-full object-cover" />
        ) : (
          <Icon name="gift" size={24} />
        )}
      </div>

      <div className="flex min-w-0 flex-1 flex-col justify-center text-xs leading-5">
        {host && <span className="truncate font-semibold text-content">{host}</span>}
        <div className="flex min-w-0 flex-col">
          {status === "loading" && <span className="text-muted">Ищем название и фото…</span>}
          {status === "success" && hasMetadata && (
            <>
              {resolvedTitle && <span className="truncate text-muted">{resolvedTitle}</span>}
              <span className="text-muted">Данные найдены — их можно изменить.</span>
            </>
          )}
          {status === "success" && !hasMetadata && (
            <span className="text-muted">Данные не найдены. Заполните желание вручную.</span>
          )}
          {status === "error" && (
            <span className="text-muted">Не удалось загрузить данные. Заполните желание вручную.</span>
          )}
        </div>
        {status === "error" && onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="min-h-11 self-start rounded-lg px-2 font-semibold text-action transition-transform active:scale-[0.96] focus-visible:outline-offset-1"
          >
            Повторить
          </button>
        )}
      </div>
    </div>
  );
}
